import {
    chain, get, isEmpty, set, size,
} from 'lodash';

import React from 'react';
import { formatDate } from '../utils/dateUtils';
import clockIcon from '../assets/image/icons/clock.svg';
import { positionEventFlowTypes, positionEvents } from '../constants/Position';
import {
    getFirstActiveJobDate,
} from '../modules/employer/positions/EmployerPositionsUtils';
import Employer from './Employer';

class Position {
    constructor(data) {
        for (const [key, value] of Object.entries(data || {})) {
            set(this, key, value);
        }
    }

    static getFormattedPositionInfo = (position, t, isListView, employer, showCurrentStatusTimestamp) => {
        const defaultLocation = get(position, 'locations.0');
        if (!defaultLocation) {
            return '';
        }

        const {
            city, region, state, country,
        } = defaultLocation;

        const additionalLocationsCount = position.locations.slice(1).length;

        const defaultLocationStr = [city, region, state, country].filter((locationItem) => locationItem).join(', ');
        const additionalLocationsStr = additionalLocationsCount ? `<span>+${additionalLocationsCount} ${t('MORE')}</span>` : '';

        const atsJobId = Position.getFormattedAtsJobId(position, t, isListView);
        const isAtsEmployer = new Employer(employer).isAtsEmployer();
        const isSyncedWithAts = atsJobId && isAtsEmployer;

        const postedDate = Position.getFormattedPostedDate(position, t, isListView, showCurrentStatusTimestamp);
        const jobDateWithIcon = Position.getJobDateWithIcon(postedDate);

        return `${defaultLocationStr} ${additionalLocationsStr} ${jobDateWithIcon} ${isSyncedWithAts ? atsJobId : ''}`;
    };

    static getFormattedAtsJobId = (position, t, isListView) => {
        const atsJobId = get(position, 'atsSettings.atsJobId');

        if (!atsJobId) {
            return '';
        }

        return isListView
            ? `<br><span>${t('ATS_ID')}${atsJobId}</span>`
            : `<span> &nbsp;•&nbsp; ${t('ATS_ID')}${atsJobId}</span>`;
    };

    static getFormattedPostedDate = (position, t, isListView, showCurrentStatusTimestamp = false) => {
        if (isListView) {
            return '';
        }
        let postedDate;
        if (showCurrentStatusTimestamp) {
            postedDate = position?.status?.timestamp || position?.created;
        } else {
            postedDate = getFirstActiveJobDate(position);
        }

        return formatDate(postedDate, 'MM/DD/YY');
    };

    static getJobDateWithIcon = (formattedDate) => (`<span> &nbsp;•&nbsp; <span style="
            vertical-align: sub;
            display: inline-block;
            background-image: url(${clockIcon});
            background-repeat: no-repeat;
            background-size: cover;
            width: 16px;
            height: 16px;
            margin-right: 4px;
        "></span> ${formattedDate}</span>`);

    static getFormattedAtsJobIdTooltip = (position, t, isListView, employer) => {
        const atsJobId = get(position, 'atsSettings.atsJobId');
        const isAtsEmployer = new Employer(employer).isAtsEmployer();
        const isSyncedWithAts = isAtsEmployer && atsJobId;
        if (isSyncedWithAts && isListView) {
            return t(
                'JOB_LIST_MULTIPLE_LOCATION_ATS_ID_INFO_TOOLTIP',
                { atsJobId },
            );
        }

        return '';
    };

    static getFormattedLocations = (position) => {
        const locations = get(position, 'locations', []);

        if (!locations.length) {
            return '';
        }

        return locations
            .map(({
                city, region, state, country,
            }) => [city, region, state, country].filter(Boolean).join(', '));
    };

    static getLocationsTooltip = (position) => {
        const [, ...rest] = get(position, 'locations', []);
        const additionalLocationsFormatted = rest.map(({
            city, region, state, country,
        }) => [city, region, state, country].filter(Boolean).join(', '));
        if (size(additionalLocationsFormatted)) {
            return additionalLocationsFormatted.map((l, index) => <div key={index}>{ l }</div>);
        }
        return '';
    };

    static getLocationsTooltipWithAtsJobId = (position, t, isListView, employer) => {
        const [, ...rest] = get(position, 'locations', []);
        const additionalLocationsFormatted = rest.map(({
            city, region, state, country,
        }) => [city, region, state, country].filter(Boolean).join(', '));

        const atsJobIdPart = Position.getFormattedAtsJobIdTooltip(position, t, isListView, employer);
        const tooltip = [...additionalLocationsFormatted, atsJobIdPart]
            .filter(Boolean)
            .map((l, index) => <div key={index}>{ l }</div>);

        return isEmpty(tooltip) ? '' : tooltip;
    };

    static isUploadWizardComplete = ({ position }) => {
        const isUploadWizardComplete = !!chain(position)
            .get('events')
            .find({
                flowType: positionEventFlowTypes.positionWizard,
                eventType: positionEvents.wizardComplete,
            })
            .value();

        return isUploadWizardComplete;
    };
}

export class FeedbackOnCandidate {
    candidateId;

    employerRepId;

    talentId;

    timestamp;

    freeText;

    source;

    recruiterId;

    ratingMode;

    grades;

    status;

    history;

    popupId;

    filterNames;

    userId;

    recommendations;

    constructor(data) {
        for (const [key, value] of Object.entries(data || {})) {
            set(this, key, value);
        }
    }

    // good fit

    // bad fit
}

export default Position;
